import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Trash, Minus, Plus } from "@phosphor-icons/react";
import axiosInstance from "../../utils/axios/axiosConfig";
import { toast } from "react-toastify";
import { Button, Select, Checkbox } from "../../components/ui";
import { lineNet, quoteTotals, formatMAD } from "./quote";

const VAT_RATE = 20;

const num = (v) => (v === "" || v == null ? 0 : Number(v));
const lineInput = "h-9 w-full rounded-lg border border-line bg-surface px-2 text-sm text-ink outline-none focus:border-accent focus:ring-2 focus:ring-accent/30";

const plus30 = () => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
};

export default function IssueQuoteModal({ open, order, onClose, onIssued }) {
  const [lines, setLines] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState("amount");
  const [validUntil, setValidUntil] = useState(plus30());
  const [vatEnabled, setVatEnabled] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!order) return;
    setLines(
      (order.products || []).map((p) => ({
        id: p.id,
        name: p.name,
        image: p.images?.[0],
        price: Number(p.pivot?.price ?? p.price ?? 0),
        quantity: Number(p.pivot?.quantity ?? 1),
        discount: Number(p.pivot?.discount ?? 0),
        discount_type: p.pivot?.discount_type || "percent",
        remove: false,
      }))
    );
    setDiscount(Number(order.discount || 0));
    setDiscountType(order.discount_type || "amount");
    setValidUntil(order.valid_until ? String(order.valid_until).slice(0, 10) : plus30());
    setVatEnabled(Number(order.vat_rate || 0) > 0);
  }, [order]);

  const setLine = (id, key, val) => setLines((ls) => ls.map((l) => (l.id === id ? { ...l, [key]: val } : l)));
  const activeLines = lines.filter((l) => !l.remove);

  const totals = useMemo(
    () =>
      quoteTotals({
        discount: num(discount),
        discount_type: discountType,
        products: activeLines.map((l) => ({
          pivot: { price: num(l.price), quantity: num(l.quantity), discount: num(l.discount), discount_type: l.discount_type },
        })),
      }),
    [activeLines, discount, discountType]
  );

  const vatRate = vatEnabled ? VAT_RATE : 0;
  const vatAmount = (totals.total * vatRate) / 100;
  const totalTtc = totals.total + vatAmount;

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await axiosInstance.put(`/orders/${order.id}/send-quote`, {
        discount: num(discount),
        discount_type: discountType,
        vat_rate: vatRate,
        valid_until: validUntil || null,
        products: lines.map((l) => ({
          id: l.id,
          quantity: num(l.quantity) || 1,
          price: num(l.price),
          discount: num(l.discount),
          discount_type: l.discount_type,
          remove: l.remove,
        })),
      });
      toast.success(data.message || "Quotation issued — copy the link to share it.");
      onIssued?.(data.order);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to issue the quotation");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <AnimatePresence>
      {open && order ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-line bg-surface text-start"
            onClick={(e) => e.stopPropagation()}
          >
            {/* header */}
            <div className="flex items-center justify-between border-b border-line px-5 py-4">
              <div>
                <div className="text-[11px] font-medium uppercase tracking-wide text-muted">Issue quotation</div>
                <h2 className="text-base font-semibold text-ink">#{order.order_number}</h2>
              </div>
              <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink">
                <X size={17} />
              </button>
            </div>

            {/* body */}
            <div className="flex-1 space-y-3 overflow-y-auto p-5">
              <p className="text-sm text-muted">Set unit prices and remises, then issue. The quote becomes visible to the customer once issued.</p>

              {activeLines.length === 0 ? (
                <p className="rounded-xl border border-dashed border-line bg-surface-2 px-3 py-6 text-center text-sm text-faint">No items left on this quote.</p>
              ) : null}

              {lines.map((l) =>
                l.remove ? null : (
                  <div key={l.id} className="rounded-xl border border-line bg-surface-2 p-3">
                    <div className="flex items-center gap-3">
                      <img src={l.image || "/placeholder.svg"} alt="" className="h-10 w-10 rounded-lg border border-line object-cover" />
                      <p className="min-w-0 flex-1 truncate text-sm font-medium text-ink">{l.name}</p>
                      <span className="text-sm font-semibold text-ink">
                        {formatMAD(lineNet({ pivot: { price: num(l.price), quantity: num(l.quantity), discount: num(l.discount), discount_type: l.discount_type } }))}
                      </span>
                      <button onClick={() => setLine(l.id, "remove", true)} className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-red-500/10 hover:text-red-600" aria-label="Remove">
                        <Trash size={15} />
                      </button>
                    </div>
                    <div className="mt-2.5 grid grid-cols-3 gap-2">
                      <div>
                        <label className="mb-0.5 block text-[11px] text-muted">Unit price</label>
                        <input type="number" step="0.01" min={0} value={l.price} onChange={(e) => setLine(l.id, "price", e.target.value)} className={lineInput} />
                      </div>
                      <div>
                        <label className="mb-0.5 block text-[11px] text-muted">Quantity</label>
                        <div className="flex items-center">
                          <button type="button" onClick={() => setLine(l.id, "quantity", Math.max(1, num(l.quantity) - 1))} className="grid h-9 w-8 shrink-0 place-items-center rounded-l-lg border border-line bg-surface text-muted hover:text-ink"><Minus size={13} /></button>
                          <input type="number" min={1} value={l.quantity} onChange={(e) => setLine(l.id, "quantity", Math.max(1, parseInt(e.target.value || 1, 10)))} className="h-9 w-full border-y border-line bg-surface text-center text-sm text-ink outline-none" />
                          <button type="button" onClick={() => setLine(l.id, "quantity", num(l.quantity) + 1)} className="grid h-9 w-8 shrink-0 place-items-center rounded-r-lg border border-line bg-surface text-muted hover:text-ink"><Plus size={13} /></button>
                        </div>
                      </div>
                      <div>
                        <label className="mb-0.5 block text-[11px] text-muted">Remise</label>
                        <div className="flex">
                          <input
                            type="number"
                            min={0}
                            value={l.discount}
                            onChange={(e) => {
                              let v = Math.max(0, Number(e.target.value || 0));
                              if (l.discount_type === "percent") v = Math.min(100, v);
                              setLine(l.id, "discount", v);
                            }}
                            className="h-9 w-full rounded-l-lg border border-line bg-surface px-2 text-sm text-ink outline-none focus:z-10 focus:border-accent focus:ring-2 focus:ring-accent/30"
                          />
                          <button
                            type="button"
                            onClick={() => setLine(l.id, "discount_type", l.discount_type === "percent" ? "amount" : "percent")}
                            className="grid h-9 w-12 shrink-0 place-items-center rounded-r-lg border border-l-0 border-line bg-surface-2 text-xs font-semibold text-muted transition hover:text-ink"
                            title="Switch between % and MAD"
                          >
                            {l.discount_type === "percent" ? "%" : "MAD"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}

              {/* Global discount + validity */}
              <div className="grid grid-cols-1 gap-3 rounded-xl border border-line bg-surface-2 p-3 sm:grid-cols-3">
                <div>
                  <label className="mb-0.5 block text-[11px] text-muted">Global remise</label>
                  <input type="number" min={0} value={discount} onChange={(e) => setDiscount(e.target.value)} className={lineInput} />
                </div>
                <div>
                  <label className="mb-0.5 block text-[11px] text-muted">Type</label>
                  <Select
                    value={discountType}
                    onChange={(v) => setDiscountType(v || "amount")}
                    options={[
                      { value: "amount", label: "Amount (MAD)" },
                      { value: "percent", label: "Percent (%)" },
                    ]}
                  />
                </div>
                <div>
                  <label className="mb-0.5 block text-[11px] text-muted">Valid until</label>
                  <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className={lineInput} />
                </div>
              </div>

              <div className="rounded-xl border border-line bg-surface-2 p-3">
                <Checkbox checked={vatEnabled} onChange={setVatEnabled} label={`Appliquer la TVA (${VAT_RATE}%)`} />
              </div>
            </div>

            {/* footer */}
            <div className="border-t border-line px-5 py-4">
              <div className="mb-3 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-muted">Subtotal</span><span className="text-ink">{formatMAD(totals.subtotal)}</span></div>
                {totals.globalDiscount > 0 && (
                  <div className="flex justify-between"><span className="text-muted">Remise</span><span className="text-ink">- {formatMAD(totals.globalDiscount)}</span></div>
                )}
                <div className="flex justify-between border-t border-line pt-1.5"><span className="text-muted">Total HT</span><span className="text-ink">{formatMAD(totals.total)}</span></div>
                {vatEnabled && (
                  <>
                    <div className="flex justify-between"><span className="text-muted">TVA ({VAT_RATE}%)</span><span className="text-ink">{formatMAD(vatAmount)}</span></div>
                    <div className="flex items-center justify-between border-t border-line pt-1.5"><span className="font-medium text-ink">Total TTC</span><span className="text-lg font-semibold text-ink">{formatMAD(totalTtc)}</span></div>
                  </>
                )}
                {!vatEnabled && (
                  <div className="flex items-center justify-between pt-0.5">
                    <span className="font-medium text-ink">Total</span>
                    <span className="text-lg font-semibold text-ink">{formatMAD(totals.total)}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button variant="outline" onClick={onClose}>Cancel</Button>
                <Button onClick={submit} loading={saving} disabled={activeLines.length === 0}>Issue quotation</Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body
  );
}
